<script lang="ts">
  import { formatTicks, groupDigits, predict } from '../../lib/complexity';
  import type { MeterDepth } from '../../lib/router';
  import {
    DEPTH_PRESETS,
    depthPreset,
    nextSnap,
    nToPosition,
    positionToN,
    SLIDER_POSITIONS,
    SNAP_POINTS,
    snapPercent,
  } from './loop-slider';
  import Pow from './Pow.svelte';

  /**
   * ループ回数スライダー（layout-and-navigation.md §6）。動かすと回数と秒数を即時に出す
   * （計算はクライアントだけ。/api/run は送らない）。
   */
  interface Props {
    n?: number;
    depth?: MeterDepth;
  }

  let { n = $bindable(20_000), depth = 2 }: Props = $props();
  const uid = $props.id();

  const pos = $derived(nToPosition(n));
  const rows = $derived(
    DEPTH_PRESETS.map((d) => {
      const p = predict(d.ops, { N: n });
      return { d, p, countText: `${formatTicks(Math.round(p.ops))} 回` };
    }),
  );
  const selected = $derived(depthPreset(depth));
  const valueText = $derived.by(() => {
    const p = predict(selected.ops, { N: n });
    return `N = ${groupDigits(n)}、${selected.short}で ${groupDigits(p.ops)} 回`;
  });

  function oninput(e: Event) {
    const el = e.currentTarget;
    if (!(el instanceof HTMLInputElement)) return;
    n = positionToN(Number(el.value));
  }

  function onkeydown(e: KeyboardEvent) {
    const up =
      e.key === 'PageUp' || (e.shiftKey && (e.key === 'ArrowRight' || e.key === 'ArrowUp'));
    const down =
      e.key === 'PageDown' || (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowDown'));
    if (!up && !down) return;
    e.preventDefault();
    const s = nextSnap(n, up ? 1 : -1);
    if (s !== null) n = s;
  }
</script>

<div class="slider">
  <div class="slider-row">
    <label for="{uid}-n" class="num">N</label>
    <input
      id="{uid}-n"
      type="range"
      min="0"
      max={SLIDER_POSITIONS}
      step="1"
      value={pos}
      aria-valuetext={valueText}
      aria-describedby="{uid}-keys"
      data-testid="loop-slider"
      {oninput}
      {onkeydown}
    />
    <output for="{uid}-n" class="num n-out" data-testid="slider-n">{groupDigits(n)}</output>
  </div>
  <div class="ticks-row" aria-hidden="true">
    <div class="slider-ticks">
      {#each SNAP_POINTS as s, i (s)}
        <span
          class:first={i === 0}
          class:last={i === SNAP_POINTS.length - 1}
          class:minor={i === 1 || i === 3}
          style:left="{snapPercent(s)}%">{groupDigits(s)}</span
        >
      {/each}
    </div>
  </div>
  <p id="{uid}-keys" class="text-soft small">
    <kbd>←</kbd> <kbd>→</kbd> で少しずつ動きます。<kbd>Shift</kbd> + <kbd>←</kbd> <kbd>→</kbd> か
    <kbd>PageUp</kbd> / <kbd>PageDown</kbd> で 10、100、1,000 … と桁ごとに飛びます。
  </p>

  <div class="table-scroll">
    <table class="loop-table" data-testid="slider-table">
      <caption class="visually-hidden"
        >N = {groupDigits(n)} のときのループの回数と目安の秒数</caption
      >
      <thead>
        <tr
          ><th scope="col">ループの重なり方</th><th scope="col">回数</th><th scope="col"
            >かかる時間の目安</th
          ></tr
        >
      </thead>
      <tbody>
        {#each rows as r (r.d.id)}
          <tr
            class:selected={r.d.id === selected.id}
            aria-current={r.d.id === selected.id ? 'true' : undefined}
          >
            <th scope="row">
              {r.d.short}{#if r.d.id === selected.id}<span class="sel-tag">上で選択中</span>{/if}
            </th>
            <td class="num"><Pow text={r.countText} /></td>
            <td class="num">{r.p.secondsText}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
</div>

<style>
  .slider {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .slider-row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) 7rem;
    gap: var(--space-3);
    align-items: center;
  }
  .slider-row input {
    width: 100%;
  }
  .n-out {
    font-weight: var(--fw-bold);
    text-align: right;
    font-size: var(--fs-lg);
  }
  .ticks-row {
    padding-left: calc(1ch + var(--space-3));
    padding-right: calc(7rem + var(--space-3));
  }
  .slider-ticks {
    position: relative;
    height: 1.2rem;
    margin: 0 8px;
    font-size: var(--fs-xs);
    color: var(--text-soft);
    font-variant-numeric: tabular-nums;
  }
  .slider-ticks span {
    position: absolute;
    transform: translateX(-50%);
    white-space: nowrap;
  }
  .slider-ticks span.first {
    transform: none;
  }
  .slider-ticks span.last {
    transform: translateX(-100%);
  }
  /* 100,000 は 200,000 と重なるので出さない */
  .slider-ticks span:nth-child(5) {
    display: none;
  }
  .small {
    font-size: var(--fs-sm);
  }
  .table-scroll {
    overflow-x: auto;
    margin-top: var(--space-2);
  }
  .loop-table {
    width: 100%;
    font-variant-numeric: tabular-nums;
  }
  .loop-table th,
  .loop-table td {
    white-space: nowrap;
    text-align: right;
    padding: var(--space-1) var(--space-3);
    border-bottom: 1px solid var(--border);
    font-size: var(--fs-sm);
  }
  .loop-table th:first-child {
    text-align: left;
  }
  .loop-table thead th {
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
  }
  .loop-table tr.selected th,
  .loop-table tr.selected td {
    font-weight: var(--fw-bold);
    background: var(--canvas);
  }
  .sel-tag {
    margin-left: var(--space-2);
    font-size: var(--fs-xs);
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
  }
  @media (max-width: 760px) {
    .slider-row {
      grid-template-columns: auto minmax(0, 1fr);
    }
    .n-out {
      grid-column: 1 / -1;
      text-align: left;
    }
    .ticks-row {
      padding-right: 0;
    }
    .slider-ticks span.minor {
      display: none;
    }
    .loop-table th,
    .loop-table td {
      padding: var(--space-1) var(--space-2);
    }
  }
</style>
